import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Navbar from "@/components/layout/Navbar";
import GuestOverlay from "@/components/auth/GuestOverlay";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { submitKieTask, getKieApiKey, getKieCallbackUrl } from "@/lib/kie-service";
import { Progress } from "@/components/ui/progress";
import {
  Mic,
  Sparkles,
  Loader2,
  Download,
  Coins,
  History,
  Repeat,
  Trash2,
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
  Play,
  Pause,
  Volume2,
  Globe,
  User,
  ChevronDown,
  Square
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { checkTaskStatus } from "@/lib/task-checker";

// Pricing calculation helper supporting ElevenLabs
const calculateCredits = (text: string): number => {
  const charCount = text.trim().length;
  if (charCount === 0) return 0;
  
  const creditsPer1000Chars = 6;
  const minCredits = 6;
  
  const credits = Math.ceil((charCount / 1000) * creditsPer1000Chars);
  return Math.max(credits, minCredits);
};

// Language codes officially supported by ElevenLabs dialogue-v3 model
const ELEVENLABS_SUPPORTED_LANG_CODES = new Set([
  "af", "ar", "hy", "as", "az", "be", "bn", "bs", "bg", "ca", "ceb", "ny", "hr", "cs", "da", "nl", "en", "et", "fil", "fi", "fr", "gl", "ka", "de", "el", "gu", "ha", "he", "hi", "hu", "is", "id", "ga", "it", "ja", "jv", "kn", "kk", "ky", "ko", "lv", "ln", "lt", "lb", "mk", "ms", "ml", "zh", "mr", "ne", "no", "ps", "fa", "pl", "pt", "pa", "ro", "ru", "sr", "sd", "sk", "sl", "so", "es", "sw", "sv", "ta", "te", "th", "tr", "uk", "ur", "vi", "cy"
]);

const languageOptions = [
  { value: "auto", label: "Auto Detect" },
  { value: "th", label: "ไทย (Thai)" },
  { value: "en", label: "English" },
  { value: "zh", label: "中文 (Chinese)" },
  { value: "ja", label: "日本語 (Japanese)" },
  { value: "ko", label: "한국어 (Korean)" },
  { value: "vi", label: "Tiếng Việt (Vietnamese)" },
  { value: "id", label: "Bahasa Indonesia" },
  { value: "ms", label: "Bahasa Melayu" },
  { value: "fil", label: "Filipino" },
  { value: "hi", label: "हिन्दी (Hindi)" },
  { value: "ar", label: "العربية (Arabic)" },
  { value: "fr", label: "Français (French)" },
  { value: "de", label: "Deutsch (German)" },
  { value: "es", label: "Español (Spanish)" },
  { value: "pt", label: "Português (Portuguese)" },
  { value: "it", label: "Italiano (Italian)" },
  { value: "ru", label: "Русский (Russian)" },
  { value: "nl", label: "Nederlands (Dutch)" },
  { value: "pl", label: "Polski (Polish)" },
  { value: "tr", label: "Türkçe (Turkish)" },
  { value: "sv", label: "Svenska (Swedish)" },
  { value: "da", label: "Dansk (Danish)" },
  { value: "no", label: "Norsk (Norwegian)" },
  { value: "fi", label: "Suomi (Finnish)" },
];

interface VoiceOption {
  id: string;
  name: string;
  description: string;
  category: string;
  previewUrl: string;
  gender: "male" | "female";
  thaiName?: string;
}

const voiceOptions: VoiceOption[] = [
  // Female Voices
  {
    id: "Z3R5wn05IrDiVCyEkUrK",
    name: "Arabella",
    description: "Mysterious and Emotive",
    category: "Narration",
    previewUrl: "https://static.aiquickdraw.com/elevenlabs/voice/Z3R5wn05IrDiVCyEkUrK.mp3",
    gender: "female"
  },
  {
    id: "5l5f8iK3YPeGga21rQIX",
    name: "Adeline",
    description: "Feminine and Conversational",
    category: "Conversational",
    previewUrl: "https://static.aiquickdraw.com/elevenlabs/voice/5l5f8iK3YPeGga21rQIX.mp3",
    gender: "female"
  },
  {
    id: "BZgkqPqms7Kj9ulSkVzn",
    name: "Eve",
    description: "Authentic, Energetic and Happy",
    category: "Conversational",
    previewUrl: "https://static.aiquickdraw.com/elevenlabs/voice/BZgkqPqms7Kj9ulSkVzn.mp3",
    gender: "female"
  },
  {
    id: "Sm1seazb4gs7RSlUVw7c",
    name: "Anika",
    description: "Animated, Friendly and Engaging",
    category: "Conversational",
    previewUrl: "https://static.aiquickdraw.com/elevenlabs/voice/Sm1seazb4gs7RSlUVw7c.mp3",
    gender: "female"
  },
  {
    id: "FGY2WhTYpPnrIDTdsKH5",
    name: "Laura",
    description: "Enthusiast, Quirky Attitude",
    category: "Conversational",
    previewUrl: "https://static.aiquickdraw.com/elevenlabs/voice/FGY2WhTYpPnrIDTdsKH5.mp3",
    gender: "female"
  },
  {
    id: "hpp4J3VqNfWAUOO0d1Us",
    name: "Bella",
    description: "Professional, Bright, Warm",
    category: "Conversational",
    previewUrl: "https://static.aiquickdraw.com/elevenlabs/voice/hpp4J3VqNfWAUOO0d1Us.mp3",
    gender: "female"
  },
  {
    id: "uYXf8XasLslADfZ2MB4u",
    name: "Hope",
    description: "Bubbly, Gossipy and Girly",
    category: "Conversational",
    previewUrl: "https://static.aiquickdraw.com/elevenlabs/voice/uYXf8XasLslADfZ2MB4u.mp3",
    gender: "female"
  },
  {
    id: "g6xIsTj2HwM6VR4iXFCw",
    name: "Jessica",
    description: "Chatty and Friendly",
    category: "Conversational",
    previewUrl: "https://static.aiquickdraw.com/elevenlabs/voice/g6xIsTj2HwM6VR4iXFCw.mp3",
    gender: "female"
  },
  // Male Voices
  {
    id: "EkK5I93UQWFDigLMpZcX",
    name: "James",
    description: "Husky, Engaging and Bold",
    category: "Narration",
    previewUrl: "https://static.aiquickdraw.com/elevenlabs/voice/EkK5I93UQWFDigLMpZcX.mp3",
    gender: "male"
  },
  {
    id: "NNl6r8mD7vthiJatiJt1",
    name: "Bradford",
    description: "Expressive and Articulate",
    category: "Conversational",
    previewUrl: "https://static.aiquickdraw.com/elevenlabs/voice/NNl6r8mD7vthiJatiJt1.mp3",
    gender: "male"
  },
  {
    id: "1SM7GgM6IMuvQlz2BwM3",
    name: "Mark",
    description: "Casual, Relaxed and Light",
    category: "Conversational",
    previewUrl: "https://static.aiquickdraw.com/elevenlabs/voice/1SM7GgM6IMuvQlz2BwM3.mp3",
    gender: "male"
  },
  {
    id: "TX3LPaxmHKxFdv7VOQHJ",
    name: "Liam",
    description: "Energetic, Social Media Creator",
    category: "Conversational",
    previewUrl: "https://static.aiquickdraw.com/elevenlabs/voice/TX3LPaxmHKxFdv7VOQHJ.mp3",
    gender: "male"
  },
  {
    id: "nPczCjzI2devNBz1zQrb",
    name: "Brian",
    description: "Deep, Resonant and Comforting",
    category: "Conversational",
    previewUrl: "https://static.aiquickdraw.com/elevenlabs/voice/nPczCjzI2devNBz1zQrb.mp3",
    gender: "male"
  },
  {
    id: "gs0tAILXbY5DNrJrsM6F",
    name: "Jeff",
    description: "Classy, Resonating and Strong",
    category: "Conversational",
    previewUrl: "https://static.aiquickdraw.com/elevenlabs/voice/gs0tAILXbY5DNrJrsM6F.mp3",
    gender: "male"
  },
  {
    id: "vBKc2FfBKJfcZNyEt1n6",
    name: "Finn",
    description: "Youthful, Eager and Energetic",
    category: "Conversational",
    previewUrl: "https://static.aiquickdraw.com/elevenlabs/voice/vBKc2FfBKJfcZNyEt1n6.mp3",
    gender: "male"
  },
  {
    id: "LruHrtVF6PSyGItzMNHS",
    name: "Benjamin",
    description: "Deep, Warm, Calming",
    category: "Conversational",
    previewUrl: "https://static.aiquickdraw.com/elevenlabs/voice/LruHrtVF6PSyGItzMNHS.mp3",
    gender: "male"
  }
];

interface SpeechLog {
  id: string;
  prompt: string;
  result_url: string | null;
  status: string;
  created_at: string;
  model: string;
  credits_used?: number;
  error_message?: string | null;
}

const TextToSpeech = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const { user, loading: authLoading, profile, refreshProfile } = useAuth();
  const { toast } = useToast();

  const [textInput, setTextInput] = useState("");
  const [stability, setStability] = useState(0.5);
  const [languageCode, setLanguageCode] = useState("auto");
  
  const currentVoiceOptions = voiceOptions;
  const [selectedVoice, setSelectedVoice] = useState(voiceOptions[0].id);

  const [isGenerating, setIsGenerating] = useState(false);
  const [previewingVoice, setPreviewingVoice] = useState<string | null>(null);
  const previewAudioRef = useRef<HTMLAudioElement | null>(null);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState("");

  // Credits calculation with debounce
  const [calculatedCredits, setCalculatedCredits] = useState(0);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Debounced credits calculation - recalculate when user stops typing
  const recalculateCredits = useCallback((text: string) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      const credits = calculateCredits(text);
      setCalculatedCredits(credits);
    }, 300); // 300ms debounce
  }, []);

  // Update credits when text changes
  useEffect(() => {
    recalculateCredits(textInput);
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [textInput, recalculateCredits]);

  // Voice preview function
  const playVoicePreview = (voiceId: string) => {
    const voice = currentVoiceOptions.find(v => v.id === voiceId);
    if (!voice) return;

    // Stop current preview if playing
    if (previewAudioRef.current) {
      previewAudioRef.current.pause();
      previewAudioRef.current = null;
    }

    if (previewingVoice === voiceId) {
      setPreviewingVoice(null);
      return;
    }

    const audio = new Audio(voice.previewUrl);
    audio.onended = () => setPreviewingVoice(null);
    audio.onerror = () => setPreviewingVoice(null);
    audio.play();
    previewAudioRef.current = audio;
    setPreviewingVoice(voiceId);
  };

  // Cleanup preview audio on unmount
  useEffect(() => {
    return () => {
      if (previewAudioRef.current) {
        previewAudioRef.current.pause();
      }
    };
  }, []);

  // Recent speeches state
  const [recentSpeeches, setRecentSpeeches] = useState<SpeechLog[]>([]);
  const [loadingSpeeches, setLoadingSpeeches] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState<string | null>(null);

  // Audio playback state
  const [playingAudioId, setPlayingAudioId] = useState<string | null>(null);
  const [audioElements, setAudioElements] = useState<{ [key: string]: HTMLAudioElement }>({});

  const userCredits = profile?.credits || 0;
  const hasEnoughCredits = authLoading || calculatedCredits === 0 || userCredits >= calculatedCredits;

  // Fetch recent speeches
  const fetchRecentSpeeches = async () => {
    if (!user) return;
    setLoadingSpeeches(true);
    try {
      const { data, error } = await supabase
        .from("generation_logs")
        .select("id, prompt, result_url, status, created_at, model, credits_used")
        .eq("user_id", user.id)
        .eq("service_type", "speech" as any)
        .order("created_at", { ascending: false })
        .limit(10);

      if (error) throw error;
      setRecentSpeeches((data || []) as SpeechLog[]);
    } catch (err) {
      console.error("Error fetching recent speeches:", err);
      setRecentSpeeches([]);
    } finally {
      setLoadingSpeeches(false);
    }
  };

  useEffect(() => {
    if (user) {
      fetchRecentSpeeches();
    }
  }, [user]);

  // Check for prompt in state (e.g. from Reuse button in History page)
  useEffect(() => {
    if (location.state?.prompt) {
      setTextInput(location.state.prompt);
    }
  }, [location.state]);

  const handleGenerate = async () => {
    if (!textInput.trim()) {
      toast({
        title: t("กรุณากรอกข้อความ", "Please enter text"),
        variant: "destructive",
      });
      return;
    }

    if (!user || !profile) {
      toast({
        title: t("กรุณาเข้าสู่ระบบ", "Please log in"),
        variant: "destructive",
      });
      return;
    }

    // IMPORTANT: Recalculate credits at generation time to prevent manipulation
    const finalCreditsRequired = calculateCredits(textInput);

    if (finalCreditsRequired === 0) {
      toast({
        title: t("กรุณากรอกข้อความ", "Please enter text"),
        variant: "destructive",
      });
      return;
    }

    if (userCredits < finalCreditsRequired) {
      toast({
        title: t("เครดิตไม่เพียงพอ", "Insufficient credits"),
        description: t(
          `ต้องการ ${finalCreditsRequired} เครดิต คุณมี ${userCredits} เครดิต`,
          `Need ${finalCreditsRequired} credits, you have ${userCredits}`
        ),
        variant: "destructive",
      });
      return;
    }

    setIsGenerating(true);
    setGenerationProgress(10);
    setStatusMessage(t("กำลังเตรียมการ...", "Preparing..."));

    try {
      // Deduct credits (using finalCreditsRequired calculated at generation time)
      const modelName = "elevenlabs/text-to-dialogue-v3";
      console.log('🔵 [TTS] Step 1: Deducting credits...', { finalCreditsRequired, userId: user.id, modelName });
      
      const { data: deductResult, error: deductError } = await supabase.rpc(
        "deduct_credits_for_generation" as any,
        {
          p_user_id: user.id,
          p_credits_required: finalCreditsRequired,
          p_service_type: "speech",
          p_model: modelName,
          p_prompt: textInput.substring(0, 500),
        }
      );

      console.log('🔵 [TTS] Step 1 result:', { deductResult, deductError });

      if (deductError || !deductResult?.success) {
        console.error('❌ [TTS] Credit deduction failed:', deductError, deductResult);
        throw new Error(deductResult?.error || deductError?.message || "Failed to deduct credits");
      }

      const logId = deductResult.log_id;
      console.log('✅ [TTS] Step 1 complete. LogId:', logId);
      
      // ElevenLabs logic
      setGenerationProgress(20);
      setStatusMessage(t("กำลังส่งคำขอ...", "Sending request..."));

      // Get API key and callback URL
      console.log('🔵 [TTS] Step 2: Getting API key...');
      const apiKey = await getKieApiKey();
      const callbackUrl = await getKieCallbackUrl();
      console.log('🔵 [TTS] Step 2 result:', { hasApiKey: !!apiKey, callbackUrl });

      if (!apiKey) {
        throw new Error("API key not configured");
      }

      // Submit task to Kie API - using dialogue-v3 model
      const taskBody = {
        model: "elevenlabs/text-to-dialogue-v3",
        input: {
          dialogue: [
            {
              text: textInput,
              voice: selectedVoice,
            }
          ],
          stability: stability,
          language_code: languageCode === "auto" || !ELEVENLABS_SUPPORTED_LANG_CODES.has(languageCode) ? "" : languageCode,
        },
        callBackUrl: callbackUrl ? `${callbackUrl}?logId=${logId}` : undefined,
      };

      console.log('🔵 [TTS] Step 3: Submitting to Kie API...', taskBody);
      const response = await submitKieTask("/api/v1/jobs/createTask", apiKey, taskBody);
      console.log('🔵 [TTS] Step 3 response:', response);

      if (response.code !== 200 || !response.data?.taskId) {
        console.error('❌ [TTS] Kie API error:', response);
        throw new Error(response.msg || "Failed to create task");
      }

      const taskId = response.data.taskId;
      setGenerationProgress(40);
      setStatusMessage(t("กำลังสร้างเสียง...", "Generating speech..."));

      // Update log with task ID
      await supabase
        .from("generation_logs")
        .update({ status: "processing", result_url: taskId })
        .eq("id", logId);

      // Refresh profile to show updated credits
      await refreshProfile();

      toast({
        title: t("กำลังสร้างเสียง...", "Generating speech..."),
        description: t(
          "คุณสามารถดูสถานะได้ที่ประวัติด้านล่าง",
          "You can check the status in the history below"
        ),
      });

      // Refresh the list
      fetchRecentSpeeches();
    } catch (error: any) {
      console.error("Generation error:", error);
      toast({
        title: t("เกิดข้อผิดพลาด", "Error"),
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
      setGenerationProgress(0);
      setStatusMessage("");
    }
  };

  const handleCheckStatus = async (logId: string) => {
    setCheckingStatus(logId);
    try {
      const result = await checkTaskStatus(logId);
      if (result.success && result.status === "completed") {
        toast({
          title: t("เสร็จแล้ว!", "Completed!"),
          description: t("เสียงพร้อมใช้งานแล้ว", "Audio is ready"),
        });
        fetchRecentSpeeches();
      } else if (result.status === "processing") {
        toast({
          title: t("กำลังประมวลผล", "Processing"),
          description: t("กรุณารอสักครู่", "Please wait"),
        });
      } else if (result.status === "failed") {
        toast({
          title: t("ล้มเหลว", "Failed"),
          description: result.message || t("เกิดข้อผิดพลาด", "An error occurred"),
          variant: "destructive",
        });
        fetchRecentSpeeches();
      }
    } catch (error: any) {
      toast({
        title: t("เกิดข้อผิดพลาด", "Error"),
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setCheckingStatus(null);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase.from("generation_logs").delete().eq("id", id);
      if (error) throw error;
      toast({ title: t("ลบสำเร็จ", "Deleted successfully") });
      fetchRecentSpeeches();
    } catch (error: any) {
      toast({
        title: t("เกิดข้อผิดพลาด", "Error"),
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleReuse = (prompt: string) => {
    setTextInput(prompt);
    window.scrollTo(0, 0);
  };

  const handleDownload = async (url: string, filename: string) => {
    try {
      const response = await fetch(url, { mode: "cors" });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      window.open(url, "_blank");
    }
  };

  const getAudioUrl = (log: SpeechLog): string | null => {
    if (!log.result_url) return null;

    let url = log.result_url;
    if (url.startsWith("{")) {
      try {
        const parsed = JSON.parse(url);
        url = parsed.resultUrls?.[0] || parsed.url || parsed.audio_url || url;
      } catch { }
    }

    return url.startsWith("http") ? url : null;
  };

  const togglePlayAudio = (logId: string, audioUrl: string) => {
    if (playingAudioId === logId) {
      audioElements[logId]?.pause();
      setPlayingAudioId(null);
    } else {
      // Stop any currently playing audio
      if (playingAudioId && audioElements[playingAudioId]) {
        audioElements[playingAudioId].pause();
      }

      let audio = audioElements[logId];
      if (!audio) {
        audio = new Audio(audioUrl);
        audio.onended = () => setPlayingAudioId(null);
        setAudioElements((prev) => ({ ...prev, [logId]: audio }));
      }
      audio.play();
      setPlayingAudioId(logId);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("th-TH", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  const renderStatusBadge = (status: string) => {
    switch (status) {
      case "completed":
        return <Badge className="bg-green-500">{t("สำเร็จ", "Completed")}</Badge>;
      case "processing":
        return <Badge className="bg-blue-500 animate-pulse">{t("กำลังประมวลผล", "Processing")}</Badge>;
      case "failed":
        return <Badge variant="destructive">{t("ล้มเหลว", "Failed")}</Badge>;
      default:
        return <Badge variant="outline">{t("รอดำเนินการ", "Pending")}</Badge>;
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />
      {!user && <GuestOverlay />}

      <main className="pt-24 pb-16 container mx-auto px-4 max-w-6xl">
        {/* Header */}
        <div className="flex flex-col items-center mb-10 text-center">
          <Badge variant="outline" className="mb-4 py-1 px-4 text-primary border-primary/20 bg-primary/5">
            <Mic className="h-3.5 w-3.5 mr-2" />
            ElevenLabs Text-to-Speech
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold font-heading mb-4 tracking-tight">
            {t("สร้างเสียงพูด", "Text to Speech")} <span className="text-gradient">AI</span>
          </h1>
          <p className="text-muted-foreground max-w-2xl text-lg">
            {t(
              "สร้างเสียงพูดสมจริงด้วย ElevenLabs รองรับหลายภาษา และปรับแต่งความเสถียรของเสียงได้",
              "Generate realistic speech with ElevenLabs. Multi-language support with customizable voice stability."
            )}
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-8 items-start">
          {/* Main Form */}
          <div className="lg:col-span-8">
            <Card className="border-border/50 bg-card/50 backdrop-blur-md shadow-xl">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Volume2 className="h-5 w-5 text-primary" />
                  {t("ใส่ข้อความที่ต้องการพูด", "Enter Text to Speak")}
                </CardTitle>
                <CardDescription>
                  {t(
                    "พิมพ์หรือวางข้อความที่คุณต้องการให้ AI อ่านออกเสียง",
                    "Type or paste the text you want AI to read aloud"
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Text Input */}
                <div className="space-y-3">
                  <Textarea
                    placeholder={t(
                      "ตัวอย่าง: สวัสดีครับ ยินดีต้อนรับสู่ช่องของเรา วันนี้เราจะมาพูดถึง...",
                      "Example: Hello and welcome to our channel. Today we will talk about..."
                    )}
                    value={textInput}
                    onChange={(e) => setTextInput(e.target.value)}
                    className="min-h-[200px] resize-none focus-visible:ring-primary shadow-inner text-lg p-4"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>
                      {t("6 เครดิต / 1,000 ตัวอักษร", "6 credits / 1,000 chars")}
                    </span>
                    <span>
                      {textInput.length.toLocaleString()} {t("ตัวอักษร", "chars")} = <span className="text-primary font-medium">{calculatedCredits}</span> {t("เครดิต", "credits")}
                    </span>
                  </div>
                </div>

                {/* Settings */}
                (
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Language */}
                    <div className="space-y-3">
                      <label className="text-sm font-medium flex items-center gap-2">
                        <Globe className="h-4 w-4 text-primary" />
                        {t("ภาษา", "Language")}
                      </label>
                      <Select value={languageCode} onValueChange={setLanguageCode}>
                        <SelectTrigger>
                          <SelectValue placeholder={t("เลือกภาษา", "Select language")} />
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                          {languageOptions.map((lang) => (
                            <SelectItem key={lang.value} value={lang.value}>
                              {lang.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Stability */}
                    <div className="space-y-3">
                      <label className="text-sm font-medium flex items-center justify-between">
                        <span>{t("ความเสถียรของเสียง", "Voice Stability")}</span>
                        <span className="text-primary font-mono">{stability.toFixed(1)}</span>
                      </label>
                      <Slider
                        value={[stability]}
                        onValueChange={(val) => setStability(val[0])}
                        min={0}
                        max={1}
                        step={0.5}
                        className="py-2"
                      />
                      <p className="text-xs text-muted-foreground">
                        {t(
                          "ค่าต่ำ = เสียงมีความหลากหลาย, ค่าสูง = เสียงคงที่สม่ำเสมอ",
                          "Low = more variation, High = more consistent"
                        )}
                      </p>
                    </div>
                  </div>
                )

                {/* Voice Selection */}
                <div className="space-y-3">
                  <label className="text-sm font-medium flex items-center gap-2">
                    <User className="h-4 w-4 text-primary" />
                    {t("เลือกเสียงผู้พูด", "Select Voice")}
                  </label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {currentVoiceOptions.map((voice) => {
                      const isSelected = selectedVoice === voice.id;
                      const isPreviewing = previewingVoice === voice.id;
                      return (
                        <div
                          key={voice.id}
                          className={`relative flex items-center gap-3 rounded-xl border px-3 py-3 transition-all cursor-pointer ${isSelected
                            ? "border-primary bg-primary/5 ring-1 ring-primary"
                            : "border-border hover:border-primary/50 hover:bg-muted/50"
                            }`}
                          onClick={() => setSelectedVoice(voice.id)}
                        >
                          {/* Voice Icon */}
                          <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${voice.gender === "female"
                            ? "bg-pink-500/10 text-pink-500"
                            : "bg-blue-500/10 text-blue-500"
                            }`}>
                            <Volume2 className="h-5 w-5" />
                          </div>

                          {/* Voice Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm truncate">
                                {voice.thaiName && localStorage.getItem("language") !== "en" ? voice.thaiName : voice.name}
                              </span>
                              <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4">
                                {voice.gender === "female" ? t("หญิง", "F") : t("ชาย", "M")}
                              </Badge>
                            </div>
                            <div className="text-xs text-muted-foreground truncate">{voice.description}</div>
                            <div className="text-[10px] text-muted-foreground/60">{voice.category}</div>
                          </div>

                          {/* Preview Button */}
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            className={`h-8 w-8 shrink-0 rounded-full ${isPreviewing ? "bg-primary/10 text-primary" : "hover:bg-muted"
                              }`}
                            onClick={(e) => {
                              e.stopPropagation();
                              playVoicePreview(voice.id);
                            }}
                          >
                            {isPreviewing ? (
                              <Square className="h-3 w-3 fill-current" />
                            ) : (
                              <Play className="h-3 w-3 ml-0.5" />
                            )}
                          </Button>

                          {/* Selected Indicator */}
                          {isSelected && (
                            <div className="absolute top-2 right-2">
                              <CheckCircle2 className="h-4 w-4 text-primary" />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Generate Button */}
                <Button
                  size="lg"
                  className="w-full h-14 text-lg font-bold shadow-lg shadow-primary/20 hover:shadow-primary/40 transition-all duration-300"
                  onClick={handleGenerate}
                  disabled={isGenerating || !textInput.trim() || !hasEnoughCredits || calculatedCredits === 0}
                >
                  {isGenerating ? (
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  ) : (
                    <Sparkles className="mr-2 h-5 w-5" />
                  )}
                  {t("สร้างเสียงพูด", "Generate Speech")}
                  <span className="ml-2 opacity-70 text-sm font-normal">
                    ({calculatedCredits > 0 ? calculatedCredits : "-"} {t("เครดิต", "Credits")})
                  </span>
                </Button>

                {!hasEnoughCredits && calculatedCredits > 0 && (
                  <p className="text-center text-sm text-destructive">
                    {t(
                      `เครดิตไม่เพียงพอ (ต้องการ ${calculatedCredits} เครดิต)`,
                      `Insufficient credits (need ${calculatedCredits} credits)`
                    )}
                  </p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-4 space-y-6">
            {/* Progress Card */}
            {isGenerating && (
              <Card className="border-primary/30 bg-primary/5 backdrop-blur-md shadow-2xl overflow-hidden relative animate-in zoom-in-95 duration-500">
                <CardContent className="pt-8 pb-8 flex flex-col items-center text-center relative z-10">
                  <div className="relative mb-6">
                    <div className="absolute inset-0 bg-primary/20 rounded-full blur-xl animate-pulse"></div>
                    <div className="bg-background/80 backdrop-blur-sm rounded-full p-4 border border-primary/20 shadow-xl relative">
                      <Loader2 className="h-10 w-10 text-primary animate-spin" />
                    </div>
                  </div>

                  <h3 className="text-xl font-bold mb-2 text-foreground">
                    {t("กำลังสร้างเสียง...", "Generating speech...")}
                  </h3>
                  <p className="text-sm text-muted-foreground mb-8 max-w-[200px]">
                    {statusMessage}
                  </p>

                  <div className="w-full space-y-3 px-2">
                    <Progress value={generationProgress} className="h-2.5 bg-primary/10" />
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Credits Card */}
            <Card className="border-border/50 bg-card/50 backdrop-blur-md shadow-lg overflow-hidden relative group">
              <CardHeader className="pb-3">
                <CardTitle className="text-md flex items-center justify-between">
                  {t("เครดิตของคุณ", "Your Credits")}
                  <Coins className="h-4 w-4 text-amber-500" />
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-baseline gap-2 mb-4">
                  <span className="text-4xl font-bold text-gradient">{profile?.credits || 0}</span>
                  <span className="text-muted-foreground text-sm uppercase tracking-wider font-medium">
                    {t("เครดิตคงเหลือ", "Credits")}
                  </span>
                </div>
                <Button
                  variant="hero"
                  size="sm"
                  className="w-full rounded-full"
                  onClick={() => navigate("/pricing")}
                >
                  {t("เติมเครดิต", "Top Up Credits")}
                </Button>
              </CardContent>
            </Card>

            {/* Info Card */}
            <Card className="border-border/50 bg-card/50 backdrop-blur-md">
              <CardContent className="pt-6">
                <div className="space-y-3 text-sm">
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5" />
                    <span className="text-muted-foreground">
                      {t("รองรับมากกว่า 25 ภาษา", "Supports 25+ languages")}
                    </span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5" />
                    <span className="text-muted-foreground">
                      {t("เสียงสมจริงระดับมืออาชีพ", "Professional-grade realistic voice")}
                    </span>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5" />
                    <span className="text-muted-foreground">
                      {t("ปรับแต่งความเสถียรของเสียงได้", "Customizable voice stability")}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Recent History Section */}
        <div className="mt-12">
          <Card className="rounded-3xl border border-border/50 shadow-xl bg-card/50 backdrop-blur-md p-6 md:p-8">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-2xl font-heading font-bold flex items-center gap-2">
                  <History className="h-6 w-6 text-primary" />
                  {t("เสียงพูดล่าสุด", "Recent Speeches")}
                </h2>
                <p className="text-sm text-muted-foreground">
                  {t("ผลลัพธ์ที่คุณสร้างล่าสุด", "Your recently generated results")}
                </p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={fetchRecentSpeeches}
                disabled={loadingSpeeches}
                className="rounded-full h-10 w-10"
              >
                <RefreshCw className={`h-5 w-5 ${loadingSpeeches ? "animate-spin" : ""}`} />
              </Button>
            </div>

            {loadingSpeeches ? (
              <div className="flex flex-col items-center justify-center py-20 grayscale opacity-50">
                <Loader2 className="h-10 w-10 animate-spin mb-4" />
                <p>{t("กำลังโหลดข้อมูล...", "Loading...")}</p>
              </div>
            ) : recentSpeeches.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {recentSpeeches.map((log) => {
                  const isCompleted = log.status === "completed";
                  const isFailed = log.status === "failed";
                  const isProcessing = log.status === "processing" || log.status === "pending";
                  const audioUrl = getAudioUrl(log);

                  return (
                    <Card
                      key={log.id}
                      className="group overflow-hidden border border-border/40 shadow-lg bg-background/60 backdrop-blur-sm hover:shadow-2xl hover:border-primary/30 transition-all duration-500 rounded-3xl flex flex-col h-full"
                    >
                      {/* Audio Player Section */}
                      <div className="p-4">
                        <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-violet-500/10 to-purple-600/10 border border-border/10 shadow-inner aspect-video flex flex-col items-center justify-center">
                          {isCompleted && audioUrl ? (
                            <>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="w-16 h-16 rounded-full bg-primary/10 hover:bg-primary/20 text-primary"
                                onClick={() => togglePlayAudio(log.id, audioUrl)}
                              >
                                {playingAudioId === log.id ? (
                                  <Pause className="h-8 w-8" />
                                ) : (
                                  <Play className="h-8 w-8 ml-1" />
                                )}
                              </Button>
                              <audio
                                src={audioUrl}
                                className="w-full mt-4"
                                controls
                                preload="metadata"
                              />
                            </>
                          ) : isFailed ? (
                            <>
                              <AlertTriangle className="h-10 w-10 text-destructive/50" />
                              <p className="text-[10px] uppercase font-bold tracking-widest text-destructive/60 mt-2">
                                {t("ล้มเหลว", "Failed")}
                              </p>
                              {log.error_message && (
                                <p className="text-[10px] text-destructive/50 mt-1 px-4 text-center line-clamp-2">
                                  {log.error_message}
                                </p>
                              )}
                            </>
                          ) : (
                            <>
                              <Loader2 className="h-10 w-10 text-primary/30 animate-spin" />
                              <p className="text-[10px] uppercase font-bold tracking-widest text-primary/40 animate-pulse mt-2">
                                {t("กำลังสร้าง...", "Generating")}
                              </p>
                              {isProcessing && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleCheckStatus(log.id)}
                                  className="mt-3 h-7 rounded-full text-[10px] px-3 border-primary/20 hover:bg-primary/5"
                                  disabled={checkingStatus === log.id}
                                >
                                  {checkingStatus === log.id ? (
                                    <Loader2 className="h-3 w-3 animate-spin mr-1" />
                                  ) : (
                                    <RefreshCw className="h-3 w-3 mr-1" />
                                  )}
                                  {t("เช็กสถานะ", "Check Status")}
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </div>

                      {/* Details Section */}
                      <CardContent className="p-5 pt-0 flex-1 flex flex-col">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-[10px] font-black uppercase tracking-widest text-primary/60 bg-primary/5 px-2 py-0.5 rounded-full border border-primary/10">
                            {log.model?.includes("botnoi") ? "Botnoi Voice" : "ElevenLabs"}
                          </span>
                          {renderStatusBadge(log.status)}
                        </div>

                        <p className="text-sm font-medium line-clamp-2 italic text-foreground/80 mb-6 flex-1">
                          "{log.prompt}"
                        </p>

                        <div className="flex items-center justify-between pt-4 border-t border-border/30">
                          <div className="flex flex-col">
                            <span className="text-[10px] text-muted-foreground font-bold uppercase tracking-tighter mb-0.5">
                              {t("วันที่", "Date")}
                            </span>
                            <span className="text-[11px] font-semibold text-foreground/70">
                              {formatDate(log.created_at)}
                            </span>
                          </div>

                          <div className="flex items-center gap-1">
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8 text-muted-foreground hover:text-primary hover:bg-primary/5 rounded-xl transition-all"
                              onClick={() => handleReuse(log.prompt)}
                              title={t("ใช้ซ้ำ", "Reuse")}
                            >
                              <Repeat className="h-4 w-4" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/5 rounded-xl transition-all"
                              onClick={() => handleDelete(log.id)}
                              title={t("ลบ", "Delete")}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                            {isCompleted && audioUrl && (
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8 text-primary hover:bg-primary/5 rounded-xl transition-all"
                                onClick={() => handleDownload(audioUrl, `speech-${log.id.slice(0, 8)}.mp3`)}
                                title={t("ดาวน์โหลด", "Download")}
                              >
                                <Download className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-24 text-muted-foreground grayscale opacity-30">
                <Mic className="h-16 w-16 mb-4" />
                <p className="text-lg font-medium tracking-tight">
                  {t("ยังไม่มีประวัติการสร้าง", "No history yet")}
                </p>
              </div>
            )}
          </Card>
        </div>
      </main>
    </div>
  );
};

export default TextToSpeech;
